import { useCallback, useEffect, useRef, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { AuthState } from "context/AuthContext";
import {
	getTargetVsAchievedPivot,
	getTargetVsAchievedTargetsMeta,
	PAGE_SIZE,
	uploadTargetVsAchieved,
} from "./api";
import PivotVirtualTable from "./PivotVirtualTable";
import { getSectionFilterOptions, resolveRowSection } from "./sectionData";

const ACCEPTED_UPLOAD_EXTENSIONS = [".csv", ".xlsx"];
const BRAND_OPTIONS = ["RARE ONES", "RARE RABBIT", "RAREISM", "RTPL"];
const DIVISION_OPTIONS = ["APPARELS", "INNERV", "FOOT WEAR"];

const today = () => format(new Date(), "yyyy-MM-dd");
const yearStart = () => format(new Date(new Date().getFullYear(), 0, 1), "yyyy-MM-dd");

const rowDedupeKey = (row) => `${row.brand}|${row.division ?? ""}|${row.section ?? ""}|${row.date}`;

const dedupeAppendRows = (existing, incoming) => {
	const seen = new Set(existing.map(rowDedupeKey));
	const next = [...existing];
	for (const row of incoming) {
		const key = rowDedupeKey(row);
		if (!seen.has(key)) {
			seen.add(key);
			next.push(row);
		}
	}
	return next;
};

const getExportGroupPrefix = (column) => {
	const label = (column?.label || "").trim();
	if (label) {
		if (label.endsWith(" Total")) return label.slice(0, -" Total".length);
		const dashIdx = label.indexOf(" - ");
		if (dashIdx !== -1) return label.slice(0, dashIdx).trim();
		return label;
	}

	const key = (column?.key || "").trim();
	if (!key) return "";
	return key
		.split("_")
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
};

const buildExportSheet = (columns, rows, { divisionFallback = "", sectionFallback = "" } = {}) => {
	if (!columns?.length || !rows?.length) return { aoa: [], merges: [] };

	const sectionRow = ["Brand", "Division", "Section", "Month", "Day"];
	const headerRow = ["", "", "", "", ""];
	columns.forEach((col) => {
		const groupPrefix = getExportGroupPrefix(col);
		col.metrics.forEach((metric) => {
			headerRow.push(`${groupPrefix} ${metric.label}`);
		});
	});
	const merges = [
		{ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
		{ s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
		{ s: { r: 0, c: 2 }, e: { r: 1, c: 2 } },
		{ s: { r: 0, c: 3 }, e: { r: 1, c: 3 } },
		{ s: { r: 0, c: 4 }, e: { r: 1, c: 4 } },
	];

	let colIndex = 5;
	columns.forEach((col) => {
		const span = col.metrics.length;
		sectionRow.push(col.label);
		for (let i = 1; i < span; i += 1) sectionRow.push("");
		if (span > 1) {
			merges.push({ s: { r: 0, c: colIndex }, e: { r: 0, c: colIndex + span - 1 } });
		}
		colIndex += span;
	});

	const dataRows = rows.map((row) => {
		const base = [
			row.brand ?? "",
			row.division ?? divisionFallback ?? "",
			resolveRowSection(row, sectionFallback),
			row.month ?? "",
			row.day ?? "",
		];
		const values = columns.flatMap((col) => {
			const section = row.sections?.[col.key] || {};
			return col.metrics.map((metric) => section[metric.key] ?? "");
		});
		return [...base, ...values];
	});

	return { aoa: [sectionRow, headerRow, ...dataRows], merges };
};

const TargetVsAchieved = () => {
	const { user } = AuthState();
	const fileInputRef = useRef(null);
	const fetchInFlightRef = useRef(false);

	const [brands, setBrands] = useState([]);
	const [divisions, setDivisions] = useState([]);
	const [sections, setSections] = useState([]);
	const [startDate, setStartDate] = useState(yearStart());
	const [endDate, setEndDate] = useState(today());

	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);
	const [pagination, setPagination] = useState(null);
	const [reportRange, setReportRange] = useState(null);

	const [loadingInitial, setLoadingInitial] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState("");

	const [targetsMeta, setTargetsMeta] = useState(null);
	const [targetsMetaLoading, setTargetsMetaLoading] = useState(true);
	const [targetsMetaError, setTargetsMetaError] = useState("");

	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState("");
	const [uploadResult, setUploadResult] = useState(null);
	const [uploadFile, setUploadFile] = useState(null);
	const [replaceExisting, setReplaceExisting] = useState(true);
	const [reportRequested, setReportRequested] = useState(false);

	const buildPivotParams = useCallback(
		(page) => {
			const params = {
				page,
				page_size: PAGE_SIZE,
				brand: brands.join(","),
			};
			if (startDate) params.start_date = startDate;
			if (endDate) params.end_date = endDate;
			if (divisions.length) params.division = divisions.join(",");
			if (sections.length) params.section = sections.join(",");
			return params;
		},
		[brands, divisions, sections, startDate, endDate]
	);

	const fetchTargetsMeta = useCallback(async () => {
		setTargetsMetaLoading(true);
		setTargetsMetaError("");
		try {
			const meta = await getTargetVsAchievedTargetsMeta();
			setTargetsMeta(meta);
		} catch (err) {
			setTargetsMetaError(
				err?.response?.data?.error || err?.response?.data?.message || err.message || "Failed to load targets meta"
			);
		} finally {
			setTargetsMetaLoading(false);
		}
	}, []);

	const fetchPivotPage = useCallback(
		async (page, { reset = false } = {}) => {
			if (!brands.length) {
				setError("Select at least one brand.");
				return;
			}
			if (fetchInFlightRef.current) return;

			fetchInFlightRef.current = true;
			if (reset || page === 1) setLoadingInitial(true);
			else setLoadingMore(true);
			setError("");

			try {
				const data = await getTargetVsAchievedPivot(buildPivotParams(page));

				setColumns(data.columns || []);
				setReportRange({
					start_date: data.start_date,
					end_date: data.end_date,
					cached: data.cached,
					cache_ttl_seconds: data.cache_ttl_seconds,
				});
				setPagination(data.pagination || null);

				if (reset || page === 1) {
					setRows(data.rows || []);
				} else {
					setRows((prev) => dedupeAppendRows(prev, data.rows || []));
				}
			} catch (err) {
				setError(err?.response?.data?.error || err?.response?.data?.message || err.message || "Failed to load report");
				if (reset) {
					setRows([]);
					setColumns([]);
					setPagination(null);
				}
			} finally {
				setLoadingInitial(false);
				setLoadingMore(false);
				fetchInFlightRef.current = false;
			}
		},
		[brands, buildPivotParams]
	);

	const resetAndFetchFirstPage = useCallback(() => {
		setRows([]);
		setPagination(null);
		setReportRange(null);
		fetchPivotPage(1, { reset: true });
	}, [fetchPivotPage]);

	const handleApplyFilters = () => {
		setReportRequested(true);
		resetAndFetchFirstPage();
	};

	const loadMore = useCallback(() => {
		if (!pagination?.has_more || loadingMore || loadingInitial || fetchInFlightRef.current) return;
		const nextPage = (pagination.page ?? 1) + 1;
		fetchPivotPage(nextPage);
	}, [pagination, loadingMore, loadingInitial, fetchPivotPage]);

	useEffect(() => {
		fetchTargetsMeta();
	}, [fetchTargetsMeta]);

	const divisionFallback = divisions.length === 1 ? divisions[0] : "";
	const sectionFilterOptions = getSectionFilterOptions(targetsMeta);
	const sectionFallback = sections.length === 1 ? sections[0] : "";

	const exportExcel = () => {
		if (!rows.length || !columns.length) return;
		const { aoa, merges } = buildExportSheet(columns, rows, {
			divisionFallback,
			sectionFallback,
		});
		const ws = XLSX.utils.aoa_to_sheet(aoa);
		if (merges.length) ws["!merges"] = merges;
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "Target vs Achieved");
		const suffix = pagination?.has_more ? "_partial" : "";
		const brandSuffix = brands.map((b) => b.replace(/\s+/g, "_")).join("-");
		const divisionSuffix = divisions.length ? `_${divisions.map((d) => d.replace(/\s+/g, "_")).join("-")}` : "";
		const sectionSuffix = sections.length ? `_${sections.map((s) => s.replace(/\s+/g, "_")).join("-")}` : "";
		XLSX.writeFile(
			wb,
			`target_vs_achieved_${brandSuffix}${divisionSuffix}${sectionSuffix}_${startDate}_${endDate}${suffix}.xlsx`
		);
	};

	const onPickUploadFile = (e) => {
		setUploadError("");
		setUploadResult(null);
		const file = e.target.files?.[0];
		if (!file) {
			setUploadFile(null);
			return;
		}
		const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
		if (!ACCEPTED_UPLOAD_EXTENSIONS.includes(ext)) {
			setUploadFile(null);
			setUploadError("Only CSV or Excel (.xlsx) files are allowed.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		setUploadFile(file);
	};

	const handleUpload = async () => {
		if (!uploadFile) {
			setUploadError("Select a CSV or Excel file to upload.");
			return;
		}
		setUploadError("");
		setUploadResult(null);
		setUploading(true);
		try {
			const data = await uploadTargetVsAchieved({
				file: uploadFile,
				replace: replaceExisting,
				email: user?.email || "",
			});
			setUploadResult(data);
			setUploadFile(null);
			if (fileInputRef.current) fileInputRef.current.value = "";
			await fetchTargetsMeta();
			if (reportRequested) {
				resetAndFetchFirstPage();
			}
		} catch (err) {
			setUploadError(
				err?.response?.data?.error || err?.response?.data?.message || err.message || "Upload failed"
			);
		} finally {
			setUploading(false);
		}
	};

	const handleMultiSelectChange = (setter) => (e) => {
		const value = e.target.value;
		setter(typeof value === "string" ? value.split(",") : value);
	};

	const renderMultiSelectValue = (selected) => (selected.length ? selected.join(", ") : "Select…");
	const renderAllMultiSelectValue = (selected) => (selected.length ? selected.join(", ") : "All");
	const brandOptions = targetsMeta?.brands?.length ? targetsMeta.brands : BRAND_OPTIONS;
	const divisionOptions = targetsMeta?.divisions?.length ? targetsMeta.divisions : DIVISION_OPTIONS;

	return (
		<Box sx={{ p: 2 }}>
			<Typography variant="h6" sx={{ mb: 0.5 }}>
				Target vs Achieved
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Daily GMV and RSV target vs achievement by channel section. Data loads in pages of {PAGE_SIZE} days per brand.
			</Typography>

			{targetsMetaLoading ? (
				<Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={16} />}>
					Checking uploaded targets…
				</Alert>
			) : null}
			{targetsMetaError ? (
				<Alert severity="warning" sx={{ mb: 2 }}>
					{targetsMetaError}
				</Alert>
			) : null}
			{targetsMeta && !targetsMetaLoading ? (
				<Alert
					severity={targetsMeta.row_count > 0 || targetsMeta.has_data ? "success" : "warning"}
					sx={{ mb: 2 }}>
					{targetsMeta.row_count > 0 || targetsMeta.has_data
						? `Targets loaded: ${targetsMeta.row_count ?? "—"} row(s)${
								targetsMeta.brands?.length ? ` · Brands: ${targetsMeta.brands.join(", ")}` : ""
							}${targetsMeta.updated_at ? ` · Updated ${targetsMeta.updated_at}` : ""}`
						: "No target data uploaded yet. Upload a targets file to populate the report."}
				</Alert>
			) : null}

			<Paper sx={{ p: 2, mb: 2 }}>
				<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
					Upload targets
				</Typography>
				<Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
					<Button component="label" variant="outlined" startIcon={<UploadFileRoundedIcon />} disabled={uploading}>
						{uploadFile ? uploadFile.name : "Choose file"}
						<input
							ref={fileInputRef}
							type="file"
							hidden
							accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
							onChange={onPickUploadFile}
						/>
					</Button>
					<FormControlLabel
						control={
							<Checkbox
								checked={replaceExisting}
								onChange={(e) => setReplaceExisting(e.target.checked)}
								disabled={uploading}
							/>
						}
						label="Replace existing targets"
					/>
					<Button
						variant="contained"
						onClick={handleUpload}
						disabled={uploading || !uploadFile}
						startIcon={uploading ? <CircularProgress color="inherit" size={18} /> : <UploadFileRoundedIcon />}>
						{uploading ? "Uploading..." : "Upload"}
					</Button>
				</Stack>
				{uploadError ? (
					<Alert severity="error" sx={{ mb: 2 }}>
						{uploadError}
					</Alert>
				) : null}
				{uploadResult?.status === "success" ? (
					<Alert severity="success" sx={{ mb: 2 }}>
						Uploaded {uploadResult.rows_uploaded} row(s) in <strong>{uploadResult.mode}</strong> mode. Total rows in
						table: {uploadResult.row_count}.
						{uploadResult.brands?.length ? ` Brands: ${uploadResult.brands.join(", ")}.` : ""}
						{uploadResult.uploaded_by ? ` Uploaded by ${uploadResult.uploaded_by}.` : ""}
					</Alert>
				) : null}
			</Paper>

			<Paper sx={{ p: 2, mb: 2 }}>
				<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
					View report
				</Typography>
				<Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
					<FormControl size="small" sx={{ minWidth: 220 }} required>
						<InputLabel shrink>Brand</InputLabel>
						<Select
							multiple
							value={brands}
							label="Brand"
							notched
							renderValue={renderMultiSelectValue}
							onChange={handleMultiSelectChange(setBrands)}>
							{brandOptions.map((b) => (
								<MenuItem key={b} value={b}>
									<Checkbox size="small" checked={brands.indexOf(b) > -1} sx={{ py: 0, mr: 0.5 }} />
									{b}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ minWidth: 200 }}>
						<InputLabel shrink>Division</InputLabel>
						<Select
							multiple
							value={divisions}
							label="Division"
							notched
							displayEmpty
							renderValue={(selected) =>
								selected.length ? selected.join(", ") : "All"
							}
							onChange={handleMultiSelectChange(setDivisions)}>
							{divisionOptions.map((d) => (
								<MenuItem key={d} value={d}>
									<Checkbox size="small" checked={divisions.indexOf(d) > -1} sx={{ py: 0, mr: 0.5 }} />
									{d}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ minWidth: 220 }}>
						<InputLabel shrink>Section</InputLabel>
						<Select
							multiple
							value={sections}
							label="Section"
							notched
							displayEmpty
							renderValue={renderAllMultiSelectValue}
							onChange={handleMultiSelectChange(setSections)}>
							{sectionFilterOptions.map((s) => (
								<MenuItem key={s} value={s}>
									<Checkbox size="small" checked={sections.indexOf(s) > -1} sx={{ py: 0, mr: 0.5 }} />
									{s}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						label="Start Date"
						type="date"
						size="small"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						label="End Date"
						type="date"
						size="small"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
					<Button
						variant="contained"
						onClick={handleApplyFilters}
						disabled={loadingInitial || !brands.length}
						startIcon={loadingInitial ? <CircularProgress color="inherit" size={18} /> : <RefreshIcon />}>
						{loadingInitial ? "Loading..." : "Load Report"}
					</Button>
					<Button
						variant="outlined"
						onClick={exportExcel}
						disabled={!rows.length}
						startIcon={<DownloadIcon />}>
						Export Excel{pagination?.has_more ? " (loaded rows)" : ""}
					</Button>
				</Stack>
			</Paper>

			{error ? (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			) : null}

			{reportRange ? (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					Range: {reportRange.start_date} to {reportRange.end_date}
					{brands.length ? ` · Brands: ${brands.join(", ")}` : ""}
					{divisions.length ? ` · Divisions: ${divisions.join(", ")}` : ""}
					{sections.length ? ` · Sections: ${sections.join(", ")}` : ""}
					{reportRange.cached ? ` · cached (TTL ${reportRange.cache_ttl_seconds ?? "—"}s)` : ""}
				</Typography>
			) : null}

			<PivotVirtualTable
				columns={columns}
				rows={rows}
				loadingInitial={loadingInitial}
				loadingMore={loadingMore}
				hasMore={Boolean(pagination?.has_more)}
				onLoadMore={loadMore}
				loadedCount={rows.length}
				totalCount={pagination?.total_count ?? 0}
				reportRequested={reportRequested}
				divisionFallback={divisionFallback}
				sectionFallback={sectionFallback}
			/>
		</Box>
	);
};

export default TargetVsAchieved;
